/**
 * Flow field: angle grid + curve integration (Tyler Hobbs style).
 * Pure math; rendering is SVG elsewhere.
 */

export type Noise01 = (x: number, y: number) => number;

/** Artboard-centered circle mask; only set when using `flowShapeMode: "circle"`. */
export interface FlowShapeCircle {
    cx: number;
    cy: number;
    r: number;
}

export interface FlowGrid {
    left: number;
    top: number;
    cellSize: number;
    numCols: number;
    numRows: number;
    angles: Float32Array;
    /** Optional 0..1 weights (2nd noise) for step-length modulation; same layout as angles. */
    stepWeights?: Float32Array;
    /** Per-corner 1 = inside shape mask (noise filled); 0 = outside (dummy values). */
    cornerMask?: Uint8Array;
    /** Circle geometry in SVG artboard space (matches `flowShapeMode: "circle"`). */
    shapeCircle?: FlowShapeCircle;
}

export interface Point2 {
    x: number;
    y: number;
}

export interface TraceCurveOptions {
    /** 0 = no quantization */
    quantizeAngleRad?: number;
    /** 0–1 strength of 2nd noise on step length (0 = ignore stepWeights) */
    stepVariation?: number;
}

export type FlowShapeMode = "full" | "circle";

/** Optional shaping of the angle field after Perlin sampling (shared by Flow Field + Flow Agents). */
export interface FlowFieldModeOptions {
    /** Rotate the entire field after noise / blend (degrees, counterclockwise in math coords; +y is down in SVG). */
    fieldRotationDeg?: number;
    /** Reflect noise coordinates horizontally before sampling (flips large-scale bias). */
    mirrorNoiseX?: boolean;
    /** Reflect noise coordinates vertically before sampling. */
    mirrorNoiseY?: boolean;
    /** 0 = pure noise direction; 1 = pure uniform flow. Vector blend in between. */
    uniformFlowBlend?: number;
    /** Direction of the uniform component when blend is above 0 (degrees, 0° = +x to the right). */
    uniformFlowAngleDeg?: number;
    /**
     * `full` fills the rectangular grid with noise.
     * `circle` fills only corners inside a circle centered on the artboard; traces and starts use the same region.
     */
    flowShapeMode?: FlowShapeMode;
    /** Radius as a fraction of min(artboard width, height) / 2; only for `flowShapeMode: "circle"`. Default 1. */
    flowShapeCircleRadiusScale?: number;
}

/** Start-point filtering to match `buildFlowGrid` circle mask. */
export type FlowShapeStartOptions =
    | { mode: "full" }
    | { mode: "circle"; radiusScale?: number };

const MIN_CIRCLE_RADIUS_SCALE = 0.02;

/** Circle centered on the artboard; `radiusScale` clamps how much of the inscribed radius is used. */
export function getFlowShapeCircle(
    width: number,
    height: number,
    radiusScale: number,
): FlowShapeCircle {
    const s = Math.min(1, Math.max(MIN_CIRCLE_RADIUS_SCALE, radiusScale));
    const r = Math.min(width, height) * 0.5 * s;
    return { cx: width * 0.5, cy: height * 0.5, r };
}

export function isInsideFlowCircle(
    x: number,
    y: number,
    circle: FlowShapeCircle,
): boolean {
    const dx = x - circle.cx;
    const dy = y - circle.cy;
    return dx * dx + dy * dy <= circle.r * circle.r + 1e-12;
}

/** True if the point lies in the active flow region (full grid or inside the circle). */
export function isPointInFlowShape(grid: FlowGrid, x: number, y: number): boolean {
    if (!grid.shapeCircle) return true;
    return isInsideFlowCircle(x, y, grid.shapeCircle);
}

function blendDirectionWithUniform(
    noiseAngleRad: number,
    uniformAngleRad: number,
    t: number,
): number {
    if (t <= 0) return noiseAngleRad;
    if (t >= 1) return uniformAngleRad;
    const nx = Math.cos(noiseAngleRad);
    const ny = Math.sin(noiseAngleRad);
    const ux = Math.cos(uniformAngleRad);
    const uy = Math.sin(uniformAngleRad);
    const wx = (1 - t) * nx + t * ux;
    const wy = (1 - t) * ny + t * uy;
    return Math.atan2(wy, wx);
}

export function seededRNG(seed: string): () => number {
    let s = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function quantizeAngleRad(angle: number, stepRad: number): number {
    if (!stepRad || stepRad <= 0) return angle;
    return Math.round(angle / stepRad) * stepRad;
}

export function buildFlowGrid(
    width: number,
    height: number,
    marginFactor: number,
    cellSize: number,
    noiseScale: number,
    noiseAngle: Noise01,
    noiseStep?: Noise01 | null,
    stepNoiseScale?: number,
    mode?: FlowFieldModeOptions,
): FlowGrid {
    const left = -marginFactor * width;
    const top = -marginFactor * height;
    const right = width + marginFactor * width;
    const bottom = height + marginFactor * height;
    const gridW = right - left;
    const gridH = bottom - top;
    const numCols = Math.max(2, Math.floor(gridW / cellSize));
    const numRows = Math.max(2, Math.floor(gridH / cellSize));
    const nx = numCols + 1;
    const ny = numRows + 1;
    const angles = new Float32Array(nx * ny);
    const sScale = stepNoiseScale ?? noiseScale;
    const stepWeights =
        noiseStep != null ? new Float32Array(nx * ny) : undefined;

    const m = mode ?? {};
    const mx = m.mirrorNoiseX ? -1 : 1;
    const my = m.mirrorNoiseY ? -1 : 1;
    const rotRad = ((m.fieldRotationDeg ?? 0) * Math.PI) / 180;
    const uBlend = Math.min(1, Math.max(0, m.uniformFlowBlend ?? 0));
    const uAngleRad = ((m.uniformFlowAngleDeg ?? 0) * Math.PI) / 180;

    const shapeMode: FlowShapeMode = m.flowShapeMode ?? "full";
    const circleShape =
        shapeMode === "circle"
            ? getFlowShapeCircle(
                  width,
                  height,
                  m.flowShapeCircleRadiusScale ?? 1,
              )
            : null;
    const cornerMask =
        shapeMode === "circle" ? new Uint8Array(nx * ny) : undefined;

    for (let r = 0; r < ny; r++) {
        for (let c = 0; c < nx; c++) {
            const wx = left + c * cellSize;
            const wy = top + r * cellSize;
            const inside =
                !circleShape || isInsideFlowCircle(wx, wy, circleShape);
            const idx = c + r * nx;
            if (cornerMask) {
                cornerMask[idx] = inside ? 1 : 0;
            }

            if (!inside) {
                angles[idx] = 0;
                if (noiseStep && stepWeights) {
                    stepWeights[idx] = 0.5;
                }
                continue;
            }

            const nc = c * mx;
            const nr = r * my;
            let a = noiseAngle(nc * noiseScale, nr * noiseScale) * Math.PI * 2;
            if (uBlend > 0) {
                a = blendDirectionWithUniform(a, uAngleRad, uBlend);
            }
            a += rotRad;
            angles[idx] = a;
            if (noiseStep && stepWeights) {
                stepWeights[idx] = noiseStep(
                    nc * sScale + 31.7,
                    nr * sScale + 12.4,
                );
            }
        }
    }

    const base: FlowGrid = {
        left,
        top,
        cellSize,
        numCols,
        numRows,
        angles,
        stepWeights,
    };
    if (cornerMask && circleShape) {
        base.cornerMask = cornerMask;
        base.shapeCircle = circleShape;
    }
    return base;
}

function angleAtCorner(grid: FlowGrid, c: number, r: number): number {
    const nx = grid.numCols + 1;
    const cc = Math.max(0, Math.min(grid.numCols, c));
    const rr = Math.max(0, Math.min(grid.numRows, r));
    return grid.angles[cc + rr * nx];
}

function cornerValid(grid: FlowGrid, c: number, r: number): boolean {
    if (!grid.cornerMask) return true;
    const nx = grid.numCols + 1;
    const cc = Math.max(0, Math.min(grid.numCols, c));
    const rr = Math.max(0, Math.min(grid.numRows, r));
    return grid.cornerMask[cc + rr * nx] !== 0;
}

function weightAtCorner(grid: FlowGrid, c: number, r: number): number {
    if (!grid.stepWeights) return 0.5;
    const nx = grid.numCols + 1;
    const cc = Math.max(0, Math.min(grid.numCols, c));
    const rr = Math.max(0, Math.min(grid.numRows, r));
    return grid.stepWeights[cc + rr * nx];
}

export function sampleAngle(grid: FlowGrid, x: number, y: number): number {
    const { left, top, cellSize, numCols, numRows } = grid;
    const u = (x - left) / cellSize;
    const v = (y - top) / cellSize;

    if (!grid.cornerMask) {
        if (u <= 0 || v <= 0 || u >= numCols || v >= numRows) {
            const c = Math.max(0, Math.min(numCols, Math.round(u)));
            const r = Math.max(0, Math.min(numRows, Math.round(v)));
            return angleAtCorner(grid, c, r);
        }

        const c0 = Math.floor(u);
        const r0 = Math.floor(v);
        const fu = u - c0;
        const fv = v - r0;

        const a00 = angleAtCorner(grid, c0, r0);
        const a10 = angleAtCorner(grid, c0 + 1, r0);
        const a01 = angleAtCorner(grid, c0, r0 + 1);
        const a11 = angleAtCorner(grid, c0 + 1, r0 + 1);

        const x00 = Math.cos(a00);
        const y00 = Math.sin(a00);
        const x10 = Math.cos(a10);
        const y10 = Math.sin(a10);
        const x01 = Math.cos(a01);
        const y01 = Math.sin(a01);
        const x11 = Math.cos(a11);
        const y11 = Math.sin(a11);

        const ix0 = x00 + fu * (x10 - x00);
        const iy0 = y00 + fu * (y10 - y00);
        const ix1 = x01 + fu * (x11 - x01);
        const iy1 = y01 + fu * (y11 - y01);
        const vx = ix0 + fv * (ix1 - ix0);
        const vy = iy0 + fv * (iy1 - iy0);

        return Math.atan2(vy, vx);
    }

    if (u <= 0 || v <= 0 || u >= numCols || v >= numRows) {
        const c = Math.max(0, Math.min(numCols, Math.round(u)));
        const r = Math.max(0, Math.min(numRows, Math.round(v)));
        if (cornerValid(grid, c, r)) return angleAtCorner(grid, c, r);
        return 0;
    }

    const c0 = Math.floor(u);
    const r0 = Math.floor(v);
    const fu = u - c0;
    const fv = v - r0;
    const b00 = (1 - fu) * (1 - fv);
    const b10 = fu * (1 - fv);
    const b01 = (1 - fu) * fv;
    const b11 = fu * fv;

    const v00 = cornerValid(grid, c0, r0) ? 1 : 0;
    const v10 = cornerValid(grid, c0 + 1, r0) ? 1 : 0;
    const v01 = cornerValid(grid, c0, r0 + 1) ? 1 : 0;
    const v11 = cornerValid(grid, c0 + 1, r0 + 1) ? 1 : 0;

    const w00 = b00 * v00;
    const w10 = b10 * v10;
    const w01 = b01 * v01;
    const w11 = b11 * v11;
    const wSum = w00 + w10 + w01 + w11;

    const a00 = angleAtCorner(grid, c0, r0);
    const a10 = angleAtCorner(grid, c0 + 1, r0);
    const a01 = angleAtCorner(grid, c0, r0 + 1);
    const a11 = angleAtCorner(grid, c0 + 1, r0 + 1);

    if (wSum < 1e-12) {
        let best = 0;
        let bestW = -1;
        const cand: [number, number][] = [
            [b00 * v00, a00],
            [b10 * v10, a10],
            [b01 * v01, a01],
            [b11 * v11, a11],
        ];
        for (const [bw, ang] of cand) {
            if (bw > bestW) {
                bestW = bw;
                best = ang;
            }
        }
        return bestW >= 0 ? best : 0;
    }

    let sx = 0;
    let sy = 0;
    const add = (w: number, a: number) => {
        if (w <= 0) return;
        sx += w * Math.cos(a);
        sy += w * Math.sin(a);
    };
    add(w00, a00);
    add(w10, a10);
    add(w01, a01);
    add(w11, a11);
    return Math.atan2(sy / wSum, sx / wSum);
}

/** Bilinear sample of step weight in [0, 1]. */
export function sampleStepWeight(grid: FlowGrid, x: number, y: number): number {
    if (!grid.stepWeights) return 0.5;
    const { left, top, cellSize, numCols, numRows } = grid;
    const u = (x - left) / cellSize;
    const v = (y - top) / cellSize;

    if (!grid.cornerMask) {
        if (u <= 0 || v <= 0 || u >= numCols || v >= numRows) {
            const c = Math.max(0, Math.min(numCols, Math.round(u)));
            const r = Math.max(0, Math.min(numRows, Math.round(v)));
            return weightAtCorner(grid, c, r);
        }

        const c0 = Math.floor(u);
        const r0 = Math.floor(v);
        const fu = u - c0;
        const fv = v - r0;

        const w00 = weightAtCorner(grid, c0, r0);
        const w10 = weightAtCorner(grid, c0 + 1, r0);
        const w01 = weightAtCorner(grid, c0, r0 + 1);
        const w11 = weightAtCorner(grid, c0 + 1, r0 + 1);

        const i0 = w00 + fu * (w10 - w00);
        const i1 = w01 + fu * (w11 - w01);
        return i0 + fv * (i1 - i0);
    }

    if (u <= 0 || v <= 0 || u >= numCols || v >= numRows) {
        const c = Math.max(0, Math.min(numCols, Math.round(u)));
        const r = Math.max(0, Math.min(numRows, Math.round(v)));
        if (cornerValid(grid, c, r)) return weightAtCorner(grid, c, r);
        return 0.5;
    }

    const c0 = Math.floor(u);
    const r0 = Math.floor(v);
    const fu = u - c0;
    const fv = v - r0;
    const b00 = (1 - fu) * (1 - fv);
    const b10 = fu * (1 - fv);
    const b01 = (1 - fu) * fv;
    const b11 = fu * fv;

    const m00 = cornerValid(grid, c0, r0) ? 1 : 0;
    const m10 = cornerValid(grid, c0 + 1, r0) ? 1 : 0;
    const m01 = cornerValid(grid, c0, r0 + 1) ? 1 : 0;
    const m11 = cornerValid(grid, c0 + 1, r0 + 1) ? 1 : 0;

    const ww00 = b00 * m00;
    const ww10 = b10 * m10;
    const ww01 = b01 * m01;
    const ww11 = b11 * m11;
    const wSum = ww00 + ww10 + ww01 + ww11;

    const s00 = weightAtCorner(grid, c0, r0);
    const s10 = weightAtCorner(grid, c0 + 1, r0);
    const s01 = weightAtCorner(grid, c0, r0 + 1);
    const s11 = weightAtCorner(grid, c0 + 1, r0 + 1);

    if (wSum < 1e-12) return 0.5;
    return (
        (ww00 * s00 + ww10 * s10 + ww01 * s01 + ww11 * s11) / wSum
    );
}

function effectiveStepLength(
    grid: FlowGrid,
    x: number,
    y: number,
    baseStepLength: number,
    opts?: TraceCurveOptions,
): number {
    const variation = opts?.stepVariation ?? 0;
    if (variation <= 0 || !grid.stepWeights) return baseStepLength;
    const w = sampleStepWeight(grid, x, y);
    return baseStepLength * (1 + (w - 0.5) * 2 * variation * 0.92);
}

export function traceCurve(
    grid: FlowGrid,
    startX: number,
    startY: number,
    baseStepLength: number,
    maxSteps: number,
    opts?: TraceCurveOptions,
    direction: 1 | -1 = 1,
): Point2[] {
    const pts: Point2[] = [];
    let x = startX;
    let y = startY;
    const { left, top, numCols, numRows, cellSize } = grid;
    const xMax = left + numCols * cellSize;
    const yMax = top + numRows * cellSize;
    const q = opts?.quantizeAngleRad ?? 0;

    for (let i = 0; i < maxSteps; i++) {
        if (x < left || x > xMax || y < top || y > yMax) break;
        if (!isPointInFlowShape(grid, x, y)) break;
        pts.push({ x, y });
        let ang = sampleAngle(grid, x, y);
        if (q > 0) ang = quantizeAngleRad(ang, q);
        if (direction < 0) ang += Math.PI;
        const step = effectiveStepLength(grid, x, y, baseStepLength, opts);
        x += step * Math.cos(ang);
        y += step * Math.sin(ang);
    }

    return pts;
}

/** Integrate opposite to the field direction (angle + π). */
export function traceCurveBackward(
    grid: FlowGrid,
    startX: number,
    startY: number,
    baseStepLength: number,
    maxSteps: number,
    opts?: TraceCurveOptions,
): Point2[] {
    return traceCurve(
        grid,
        startX,
        startY,
        baseStepLength,
        maxSteps,
        opts,
        -1,
    );
}

/** Forward + backward from the same start into one polyline. */
export function traceCurveBidirectional(
    grid: FlowGrid,
    startX: number,
    startY: number,
    baseStepLength: number,
    maxSteps: number,
    opts?: TraceCurveOptions,
): Point2[] {
    const forward = traceCurve(
        grid,
        startX,
        startY,
        baseStepLength,
        maxSteps,
        opts,
        1,
    );
    const backward = traceCurve(
        grid,
        startX,
        startY,
        baseStepLength,
        maxSteps,
        opts,
        -1,
    );

    const tail = backward.length > 1 ? backward.slice(1).reverse() : [];
    const merged = [...tail, ...forward];
    if (merged.length >= 2) return merged;
    if (forward.length >= 2) return forward;
    if (backward.length >= 2) return backward.slice().reverse();
    return merged;
}

export function generateStartPointsGrid(
    width: number,
    height: number,
    spacing: number,
    shape: FlowShapeStartOptions = { mode: "full" },
): Point2[] {
    const pts: Point2[] = [];
    const s = Math.max(4, spacing);
    const circle =
        shape.mode === "circle"
            ? getFlowShapeCircle(width, height, shape.radiusScale ?? 1)
            : null;
    for (let y = s * 0.5; y < height; y += s) {
        for (let x = s * 0.5; x < width; x += s) {
            if (circle && !isInsideFlowCircle(x, y, circle)) continue;
            pts.push({ x, y });
        }
    }
    return pts;
}

export function generateStartPointsRandom(
    width: number,
    height: number,
    count: number,
    rng: () => number,
    shape: FlowShapeStartOptions = { mode: "full" },
): Point2[] {
    const pts: Point2[] = [];
    const n = Math.max(0, Math.floor(count));
    const circle =
        shape.mode === "circle"
            ? getFlowShapeCircle(width, height, shape.radiusScale ?? 1)
            : null;

    for (let i = 0; i < n; i++) {
        if (!circle) {
            pts.push({ x: rng() * width, y: rng() * height });
            continue;
        }
        let placed = false;
        for (let attempt = 0; attempt < 48; attempt++) {
            const t = rng() * 2 * Math.PI;
            const rad = circle.r * Math.sqrt(rng());
            const x = circle.cx + rad * Math.cos(t);
            const y = circle.cy + rad * Math.sin(t);
            if (x >= 0 && x <= width && y >= 0 && y <= height) {
                pts.push({ x, y });
                placed = true;
                break;
            }
        }
        if (!placed) {
            pts.push({ x: circle.cx, y: circle.cy });
        }
    }
    return pts;
}

export function pointsToPathD(points: Point2[]): string {
    if (points.length < 2) return "";
    const [p0, ...rest] = points;
    let d = `M ${p0.x} ${p0.y}`;
    for (const p of rest) {
        d += ` L ${p.x} ${p.y}`;
    }
    return d;
}

export function traceAllCurves(
    grid: FlowGrid,
    starts: Point2[],
    baseStepLength: number,
    maxSteps: number,
    options?: {
        bidirectional?: boolean;
        trace?: TraceCurveOptions;
    },
): Point2[][] {
    const curves: Point2[][] = [];
    const bidirectional = options?.bidirectional ?? false;
    const traceOpts = options?.trace;
    for (const s of starts) {
        const c = bidirectional
            ? traceCurveBidirectional(
                  grid,
                  s.x,
                  s.y,
                  baseStepLength,
                  maxSteps,
                  traceOpts,
              )
            : traceCurve(
                  grid,
                  s.x,
                  s.y,
                  baseStepLength,
                  maxSteps,
                  traceOpts,
                  1,
              );
        if (c.length >= 2) curves.push(c);
    }
    return curves;
}
